/**
 * Barrel re-export for the dashboard2-schedule domain.
 *
 * This file previously contained all schedule-related utilities. They have been
 * split into focused modules; imports from this barrel continue to work unchanged.
 *
 * Domain modules:
 *  - dashboard2-schedule-filter.utils   — schedule access, filtering, and formatting helpers
 *  - dashboard2-status-derivation.utils — DayWorkStatus derivation logic
 *  - dashboard2-colleague-enrichment.utils — colleague/team-member helpers
 */
import { DateTime } from "luxon";
import { normalizePicturePaths } from "../../../components/BookingForm/functions/normalizePicturePaths.function";
import type { IPlaceInventory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/PlaceInventory.type";
import type { IZoneInventory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/ZoneInventory.type";
import type { VehicleInventory } from "../../../components/FleetManagement/types/VehicleInventory.types";
import { isScheduleMobileWorkingLocation } from "../../../components/Schedule/schedule-event-location.component";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";

export const DASHBOARD2_WORKDAY_COUNT = 5;

/**
 * Weekend days the week planner may add after Friday.
 * Shared by the week cards and the schedule query that loads them.
 */
export type Dashboard2WeekRangeOptions = {
  /** When true, Saturday is shown after Friday. When false or omitted, Saturday is absent. */
  bookingsOnSaturdaysAllowed?: boolean;
  /**
   * When true, Sunday is shown after the last earlier day.
   * If Saturday is not allowed, Sunday follows Friday directly.
   */
  bookingsOnSundaysAllowed?: boolean;
};

/**
 * API schedule queries treat date-only `endDate` as start-of-day UTC (`LessThanOrEqual`),
 * so the last inclusive workday must be sent as the following calendar day.
 */
export function getDashboard2WeekRangeEndDateForApi(lastWorkDay: DateTime): string {
  return lastWorkDay.plus({ days: 1 }).toFormat("yyyy-MM-dd");
}

export function getDashboard2WeekRange(
  weekAnchor: DateTime,
  options?: Dashboard2WeekRangeOptions
): {
  startDate: string;
  endDate: string;
  workDays: DateTime[];
} {
  const start = weekAnchor.startOf("week");
  const workDays = Array.from({ length: DASHBOARD2_WORKDAY_COUNT }, (_, index) =>
    start.plus({ days: index })
  );

  if (options?.bookingsOnSaturdaysAllowed) {
    workDays.push(start.plus({ days: 5 }));
  }
  if (options?.bookingsOnSundaysAllowed) {
    workDays.push(start.plus({ days: 6 }));
  }

  const lastWorkDay = workDays[workDays.length - 1] ?? start;
  const endDate = getDashboard2WeekRangeEndDateForApi(lastWorkDay);

  return {
    startDate: start.toFormat("yyyy-MM-dd"),
    endDate,
    workDays
  };
}

type InventoryWithLocationPicture = {
  locationInventory?: {
    picturePath?: string | null;
  };
};

function resolveLocationPicturePaths(inventory: IPlaceInventory | IZoneInventory): string[] {
  const picturePath = (inventory as InventoryWithLocationPicture).locationInventory?.picturePath;
  return normalizePicturePaths(picturePath != null ? [picturePath] : null) ?? [];
}

export function resolveSchedulePicturePaths(schedule: BookingScheduleInterface): string[] {
  if (isScheduleMobileWorkingLocation(schedule)) {
    return [];
  }

  if (schedule.bookingType === BookingType.CONFERENCEZONE) {
    const zoneInventory = schedule.bookingInventory as IZoneInventory;
    return normalizePicturePaths(zoneInventory.picturePaths) ?? [];
  }

  if (schedule.bookingType === BookingType.VEHICLE) {
    const picturePaths =
      schedule.vehicleInfo?.picturePaths ||
      (schedule.bookingInventory as VehicleInventory)?.picturePaths;
    return normalizePicturePaths(picturePaths) ?? [];
  }

  const inventory = schedule.bookingInventory as IPlaceInventory | IZoneInventory;
  return resolveLocationPicturePaths(inventory);
}

export * from "./dashboard2-schedule-filter.utils";
export * from "./dashboard2-status-derivation.utils";
export * from "./dashboard2-colleague-enrichment.utils";
